import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from './useMultiTenant';
import { SALE_ITEM_QTY_SELECT } from '@/lib/saleItemQuantity';
import type {
  DeliveryWithDetails, 
  DeliveryFilters, 
} from '@/types/delivery';

export const useDeliveries = (filters?: DeliveryFilters) => {
  const { companyId } = useMultiTenant();
  const queryClient = useQueryClient();

  // Fetch deliveries
  const { data: deliveries, isLoading, error, refetch } = useQuery({
    queryKey: ['deliveries', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('deliveries')
        .select(`
          *,
          sale:sales(sale_number, total_value, salesperson_id, payment_status, customer:customers(name, document_number)),
          assigned_technician:profiles!deliveries_assigned_to_fkey(user_id, full_name, email, phone, avatar_url, vehicle_type),
          created_user:profiles!deliveries_created_by_fkey(full_name),
          items:delivery_items(
            *,
            sale_item:sale_items(
              ${SALE_ITEM_QTY_SELECT},
              product:products(name),
              service:services(service_name)
            )
          )
        `)
        .eq('company_id', companyId)
        // §I.94 — cases de devolução de balcão (rental_collection + pickup)
        // vivem na Triagem; coletas de driver (delivery_method='delivery'/null)
        // continuam listadas. NOT(A AND B) expandido com guarda de NULL.
        .or('origin_type.is.null,origin_type.neq.rental_collection,delivery_method.is.null,delivery_method.neq.pickup')
        .order('delivery_date', { ascending: false })
        .limit(500);

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.assignedTo) {
        if (filters.assignedTo === 'unassigned') {
          query = query.is('assigned_to', null);
        } else {
          query = query.eq('assigned_to', filters.assignedTo);
        }
      }

      if (filters?.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }

      if (filters?.startDate) {
        query = query.gte('delivery_date', format(filters.startDate, 'yyyy-MM-dd'));
      }

      if (filters?.endDate) {
        query = query.lte('delivery_date', format(filters.endDate, 'yyyy-MM-dd'));
      }

      if (filters?.searchTerm) {
        query = query.or(`delivery_number.ilike.%${filters.searchTerm}%,delivery_address.ilike.%${filters.searchTerm}%,contact_name.ilike.%${filters.searchTerm}%`);
      }

      if (filters?.region) {
        query = query.eq('city', filters.region);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      let result = data as DeliveryWithDetails[];

      // Filter by salesperson (client-side since it's a nested relation)
      if (filters?.salespersonId) {
        result = result.filter((d: any) => 
          d.sale?.salesperson_id === filters.salespersonId
        );
      }

      return result;
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  // Realtime: escutar mudanças na tabela deliveries filtrado por company_id
  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel(`deliveries-erp-${companyId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'deliveries',
        filter: `company_id=eq.${companyId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [companyId, queryClient]);

  return {
    deliveries,
    isLoading,
    isError: !!error,
    error,
    refetch,
  };
};
